import { FC, Fragment, MouseEventHandler, useMemo } from 'react';
import {
  LabelText,
  LoadingWrapper,
  NonMobileWrapper,
  Text,
  H3,
  TooltipWrapper,
} from 'components/JaneModal/views/EditJaneView/EditJaneView.styled';
import {
  BKJTextInput,
  BKJButton,
  Flex,
  BKJComboBox,
  BKJLoadingSpinner,
  BKJCheckbox,
  BKJIconTooltip,
  BKJPillDropdown,
  BKJSelect,
  BKJPhoneNumberInput,
} from '@awais4123/fillify-design-library';
import { ShiftDateCalendarInput } from 'components/ShiftDateCalendarInput';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { useFetchJaneByIdDataContext } from 'components/JaneModal/FetchJaneById.data.context';
import { BKJRadioInput } from 'components/BKJRadioInput/BKJRadioInput';
import { TeamLocationFilterInput } from 'components/JaneModal/views/EditJaneView/TeamLocationFilterInput';
import { SelectInputPositionByDepartment } from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment';
import {
  InviteTeamMemberViewFormProvider,
  useInviteTeamMemberViewFormContext,
} from 'components/InviteTeamMembersByCSVModal/views/InviteTeamMemberView/InviteTeamMemberView.form.context';
import { TeamMemberPageProvider } from 'pages/TeamMemberPage/TeamMemberPage.context';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { inviteTeaMemberCSVModalRoutingService } from 'components/InviteTeamMembersByCSVModal/InviteTeamMembersByCSVModal.ui.context';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import { useTranslation } from 'hooks/useTranslation';
import i18next from 'i18next';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import {
  employmentStatuses,
  monthToDaysMap,
} from 'components/InviteTeamMembersByCSVModal/views/InviteTeamMemberView/InviteTeamMemberView.constants';
import { BKJListItem } from '@awais4123/fillify-design-library';
import { IBKJSelectRenderOptionsProps } from '@awais4123/fillify-design-library/lib/components/BKJSelect/BKJSelect.types';
import { css } from 'styled-components';
import { MessageWrapper } from 'components/JaneModal/views/EditJaneView/EditJaneView.styled';
import React, { useRef, useEffect } from 'react';
import { startCase } from 'utils/startCase';
const Component: FC = () => {
  const { t } = useTranslation();

  const {
    values,
    onChange,
    handlePrimaryLocationChange,
    fieldErrors,
    apiErrors,
    isDisabled,
    setSelectMonth,
    dayOptions,
    onSubmit,
    handlePrimaryPosition,
    handleSecondaryPosition,
    secondaryPositionsDropdownItems,
    isDiscardConfirmationOpen,
    setIsDeleteConfirmationOpen,
  } = useInviteTeamMemberViewFormContext();
  const RenderOptions: FC<IBKJSelectRenderOptionsProps> = ({
    name,
    options,
    selected,
    onChange,
  }) => {
    return (
      <Fragment>
        {options.length === 0 ? (
          <MessageWrapper>{t('team_member:SELECT_MONTH_FIRST')}</MessageWrapper>
        ) : (
          options.map(({ key, label, value }) => (
            <BKJListItem
              name={name}
              selected={selected}
              key={key}
              label={label}
              value={value}
              onChange={onChange}
              css={css`
                background-color: white !important;
              `}
            />
          ))
        )}
      </Fragment>
    );
  };

  const employStatus = startCase(values?.employmentStatus);
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSelectMonth(e.target.value);
    if (values.birthDay) {
      values.birthDay = '';
    }
  }
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const extractApiErrorsData = (data: { email?: string; phone?: string }[] | undefined) => {
    return data?.reduce((acc, item) => {
      if (item.email) {
        acc.email = item.email;
      }
      if (item.phone) {
        acc.phone = item.phone;
      }
      return acc;
    }, {} as { email?: string; phone?: string });
  };

  const apiErrorsData = useMemo(() => extractApiErrorsData(apiErrors?.data), [apiErrors?.data]);

  useEffect(() => {
    const apiErrorsData = extractApiErrorsData(apiErrors?.data);
    if (apiErrorsData?.email && emailInputRef.current) {
      emailInputRef.current.focus();
    } else if (apiErrorsData?.phone && phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, [apiErrors]);
  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
      padding="32px 39px 0"
      borderTop="1px solid rgba(139, 150, 163, 0.33)"
    >
      <Flex flexDirection="column" gap="32px" padding="0 0 50px" width="100%">
        <H3>{t('team_member:INVITE_TEAM_MEMBER')}</H3>
        <Text>{t('team_member:ENTER_TEAM_MEMBERS_PROFILE_INFORMATION_AND_SELECT_INVITE')}</Text>
        <BKJTextInput
          label={`${t('team_member:FIRST_NAME')}*`}
          onChange={onChange}
          value={values?.firstName}
          name={'firstName'}
          autoComplete="off"
          error={fieldErrors.firstName}
        />
        <BKJTextInput
          label={`${t('team_member:LAST_NAME')}*`}
          onChange={onChange}
          value={values?.lastName}
          name={'lastName'}
          autoComplete="off"
          error={fieldErrors.lastName}
        />
        <Flex flexDirection="column" gap="8px">
          <BKJTextInput
            ref={emailInputRef}
            label={`${t('team_member:EMAIL')}*`}
            onChange={onChange}
            value={values?.email}
            name={'email'}
            autoComplete="off"
            error={fieldErrors.email || apiErrorsData?.email}
          />
        </Flex>
        <Flex flexDirection="column">
          <BKJPhoneNumberInput
            ref={phoneInputRef}
            label={`${t('team_member:MOBILE_PHONE')}*`}
            onChange={onChange}
            value={values?.phone}
            name={'phone'}
            autoComplete="off"
            error={fieldErrors.phone || apiErrorsData?.phone}
            maxLength={11}
          />
          <UserGuard
            userType={[
              UserPermissionEnum.USER_TYPE_B2B,
              UserPermissionEnum.USER_TYPE_ESSENTIAL,
              UserPermissionEnum.USER_TYPE_PREMIUM,
            ]}
          >
            <Flex alignItems="center" gap="10px" style={{ marginTop: '10px', marginLeft: '16px' }}>
              <BKJCheckbox
                variant="PurpleSolid"
                value={values?.isMobile}
                name={'isMobile'}
                onChange={onChange}
              />
              <NonMobileWrapper>{t('team_member:FLAG_AS_NON_APP_USER')}</NonMobileWrapper>
              <BKJIconTooltip iconName="Info" placement="bottom">
                <TooltipWrapper>
                  {t('team_member:STAFF_WILL_RECEIVE_AUTOMATED_PHONE')}
                </TooltipWrapper>
              </BKJIconTooltip>
            </Flex>
          </UserGuard>
        </Flex>
        {WhiteLabelAccessor.isPetalProvider && (
          <Flex flexDirection="column">
            <LabelText>{`${t('team_member:SELECT_A_LANGUAGE_PREFERENCE')}*`}</LabelText>
            <BKJRadioInput
              name="locale"
              value="en"
              onChange={onChange}
              checked={values?.locale === 'en'}
              label={t('team_member:ENGLISH')}
            />
            <BKJRadioInput
              name="locale"
              value="fr"
              onChange={onChange}
              checked={values?.locale === 'fr'}
              label={t('team_member:FRENCH')}
            />
          </Flex>
        )}
        <Flex flexDirection="column" gap="8px">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {`${t('team_member:PRIMARY_LOCATION')}*`}
          </LabelText>
          <TeamLocationFilterInput
            value={values?.location}
            onChange={handlePrimaryLocationChange}
            name="location"
            width="470px"
          />
        </Flex>
        <Flex flexDirection="column" gap="8px">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {`${t('team_member:PRIMARY_POSITION')}*`}
          </LabelText>

          <SelectInputPositionByDepartment
            dropdownVariant="Square"
            placeholder={t('team_member:SELECT_POSITION')}
            onChange={handlePrimaryPosition}
            type="Single"
            name="position_id"
            variant="GreyOutlined"
            value={values?.position_id}
            error={fieldErrors?.position_id}
            width="100%"
            key={`${values?.location?.value}`}
            allocatedToCommunities={[`${values?.location?.value}`]}
            dropdownProps={{
              dropdownListItemVariant: 'List',
              isSearchable: false,
            }}
          />
        </Flex>
        <Flex flexDirection="column">
          <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
            {t('team_member:SECONDARY_POSITIONS')}
          </LabelText>
          <BKJPillDropdown
            onChange={handleSecondaryPosition}
            name="secondaryPositionsIds"
            value={values.secondaryPositionsIds}
            options={secondaryPositionsDropdownItems}
            validations={[]}
            error={fieldErrors.secondaryPositionsIds}
            placeholder={t('team_member:SELECT_POSITIONS')}
            max={secondaryPositionsDropdownItems.length}
          />
        </Flex>
        <UserGuard
          userType={[
            UserPermissionEnum.USER_TYPE_B2B,
            UserPermissionEnum.USER_TYPE_ESSENTIAL,
            UserPermissionEnum.USER_TYPE_PREMIUM,
          ]}
        >
          <BKJComboBox
            label={`${t('team_member:EMPLOYMENT_STATUS')}*`}
            placeholder={t('team_member:SELECT_STATUS')}
            name="employmentStatus"
            value={[employStatus]}
            onChange={onChange}
            options={employmentStatuses()}
            type="Single"
            action="OpenToggle"
            filter="TimeStartsWith"
            error={fieldErrors?.employmentStatus}
          />

          <BKJTextInput
            label={t('team_member:HOURLY_RATE')}
            onChange={onChange}
            value={values?.hourlyRate}
            name={'hourlyRate'}
            autoComplete="off"
          />
        </UserGuard>
        <BKJTextInput
          label={`${t('team_member:EMPLOYEE_ID')}`}
          onChange={onChange}
          value={values?.employeeNumber}
          name={'employeeNumber'}
          autoComplete="off"
          error={fieldErrors?.employeeNumber}
        />
        <Flex flexDirection="column" gap="8px">
          <LabelText>{t('team_member:EMPLOYEE_START_DATE')}</LabelText>
          <ShiftDateCalendarInput
            name="employeeStartDate"
            value={values?.employeeStartDate}
            onChange={onChange}
            width="100%"
            error={fieldErrors?.employeeStartDate}
          />
        </Flex>
        <Flex gap="16px">
          <Flex flexDirection="column" width="100%">
            <LabelText>{t('team_member:BIRTH_MONTH')}</LabelText>
            <BKJSelect
              placeholder={t('team_member:SELECT_MONTH')}
              name="birthMonth"
              options={Object.keys(monthToDaysMap).map((month, index) => ({
                key: index,
                id: index,
                value: month,
                label: month,
              }))}
              onChange={(e) => {
                handleChange(e);
                onChange(e);
              }}
              type="Single"
              variant="GreyOutlined"
              value={[`${values?.birthMonth}`]}
              renderOptions={RenderOptions}
              placeholderColor="TextDisabled"
              error={fieldErrors.birthMonth}
            />
          </Flex>
          <Flex flexDirection="column" width="100%">
            <LabelText>{t('team_member:BIRTH_DAY')}</LabelText>
            <BKJSelect
              placeholder={t('team_member:DAY')}
              name="birthDay"
              options={dayOptions}
              onChange={(e) => {
                onChange(e);
              }}
              type="Single"
              variant="GreyOutlined"
              value={[`${values?.birthDay}`]}
              renderOptions={RenderOptions}
              placeholderColor="TextDisabled"
              error={fieldErrors.birthDay}
            />
          </Flex>
        </Flex>
      </Flex>

      <BKJSideModalFooter>
        <Flex width="100%" justifyContent="end">
          <BKJButton
            variant="PurpleOutlined"
            width="140px"
            onClick={() => setIsDeleteConfirmationOpen(true)}
            type="button"
          >
            {t('team_member:CANCEL')}
          </BKJButton>
          <BKJButton
            variant="PurpleSolid"
            disabled={isDisabled}
            width="140px"
            onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
            type="button"
            style={{ marginLeft: '4%' }}
          >
            {t('team_member:INVITE')}
          </BKJButton>
        </Flex>
      </BKJSideModalFooter>
      <BKJCenteredDialog
        isOpen={isDiscardConfirmationOpen}
        variant="Danger"
        title={i18next.t('create_shifts:DISCARD_CHANGES')}
        data-cy="discard-modal"
        text=""
        cancelLabel={i18next.t('auth:CANCEL')}
        actionLabel={i18next.t('create_shifts:DISCARD')}
        onAction={() => {
          setIsDeleteConfirmationOpen(false);
          return inviteTeaMemberCSVModalRoutingService.close();
        }}
        onCancel={() => setIsDeleteConfirmationOpen(false)}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      />
    </Flex>
  );
};

export const InviteTeamMemberDefaultFormComponent: FC = () => {
  const { isLoading } = useFetchJaneByIdDataContext();
  if (isLoading)
    return (
      <LoadingWrapper>
        <BKJLoadingSpinner variant="Default" />
      </LoadingWrapper>
    );
  return (
    <TeamMemberPageProvider>
      <InviteTeamMemberViewFormProvider>
        <Component />
      </InviteTeamMemberViewFormProvider>
    </TeamMemberPageProvider>
  );
};
export const InviteTeamMemberView: FC = () => {
  return <InviteTeamMemberDefaultFormComponent />;
};
